import { useState } from "react";
import { useAuth } from "./auth";
import { useCommunication } from "./CommunicationWrapper";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Crown } from "lucide-react";

export const UnrestrictedButtonView = ({ children, ...props }: { children: React.ReactNode;[key: string]: any }) => {
    return children;
};

export const RestrictedButtonView = ({ children, ...props }: { children: React.ReactNode;[key: string]: any }) => {
    return children;
};

export const RestrictedButton = ({ children, restrictedPlans, onClick, onRestricted, ...props }: { children: React.ReactElement<{ restrictedPlans: PlanTypes[] }>[]; restrictedPlans: PlanTypes[]; onRestricted?: () => void; onClick?: (e?: any) => void;[key: string]: any }) => {
    const { planType } = useAuth();
    const { restrictedActionCheck } = useCommunication();
    const plan: PlanTypes = planType ? planType : "none";
    const isRestricted = restrictedPlans.includes(plan as PlanTypes);

    const handleClick = (e?: any) => {
        if (restrictedActionCheck(restrictedPlans, onRestricted)) return;
        onClick?.(e);
    };

    if (!children) return null;

    const restrictedView = children?.find((child) => child.type === RestrictedButtonView);
    const unrestrictedView = children?.find((child) => child.type === UnrestrictedButtonView);

    if (isRestricted && restrictedView) {
        return (
            <button onClick={handleClick} {...props}>
                {restrictedView}
            </button>
        );
    }

    if (!isRestricted && unrestrictedView) {
        return (
            <button onClick={handleClick} {...props}>
                {unrestrictedView}
            </button>
        );
    }

    return null;
};

export const RestrictedComponent = ({ restrictedPlans, children }: { restrictedPlans: PlanTypes[]; children: React.ReactElement<{ restrictedPlans: PlanTypes[] }>[] }) => {
    const { planType } = useAuth();
    const plan: PlanTypes = planType ? planType : "none";
    const isRestricted = restrictedPlans.includes(plan as PlanTypes);

    // Find the appropriate child component to render
    const restrictedView = children.find((child) => child.type === RestrictedView);
    const unrestrictedView = children.find((child) => child.type === UnrestrictedView);

    if (isRestricted && restrictedView) {
        return restrictedView;
    }

    if (!isRestricted && unrestrictedView) {
        return unrestrictedView;
    }

    // Fallback: render nothing if no matching view is found
    return null;
};

export const RestrictedView = ({ children }: { children: React.ReactNode;[key: string]: any }) => {
    return children;
};

export const UnrestrictedView = ({ children }: { children: React.ReactNode;[key: string]: any }) => {
    return children;
};

export const RestrictedIcon = ({ restrictedPlans, children, ...props }: { restrictedPlans: PlanTypes[]; children?: React.ReactNode;[key: string]: any }) => {
    const { planType } = useAuth();
    const plan: PlanTypes = planType ? planType : "none";


    const [hovering, setHovering] = useState(false);

    // Create a fixed high z-index container only while hovered
    const [tooltipContainer, setTooltipContainer] = useState<HTMLElement | null>(null);
    // No manual cleanup needed; React will mount/unmount the container div

    if (restrictedPlans.includes(plan as PlanTypes)) {
        // Destructure children and className from props to avoid conflicts
        const { className, ...crownProps } = props;

        return (
            <div className="flex items-center gap-1" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
                {hovering && <div ref={setTooltipContainer} className="fixed z-[9999999999999] top-0 left-0 w-full h-full pointer-events-none" />}
                <Tooltip>
                    <TooltipTrigger>
                        <Crown {...crownProps} className={className} />
                    </TooltipTrigger>
                    {hovering && tooltipContainer && (
                        <TooltipContent side={props?.side || "top"} sideOffset={10}>
                            {plan === "trial" ? "You have access to this pro feature during your trial!" : "This is a pro feature!"}
                        </TooltipContent>
                    )}
                </Tooltip>
                {children}
            </div>
        );
    }

    return children;
}





export const RESTRICT_NONE: PlanTypes[] = ["none"];
export const RESTRICT_NONE_FREE: PlanTypes[] = ["none", "free"];
export const RESTRICT_NONE_FREE_TRIAL: PlanTypes[] = ["none", "free", "trial"];

export const UNRESTRICTED_LEARNPLUS_TRIAL: PlanTypes[] = ["none", "free", "style"];
export const UNRESTRICTED_LEARNPLUS_TRIAL_STYLE: PlanTypes[] = ["none", "free"];
export const UNRESTRICTED_ALL: PlanTypes[] = [];